import {
  useBeforeUnload,
  useLocation,
  useSearchParams,
} from '@remix-run/react'
import { AdvancedMarker, useMap } from '@vis.gl/react-google-maps'
import React, {
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import {
  aIsMapEasing,
  aMapFilters,
  aMapRef,
  aMapStyle,
  aMapViewState,
  aMarkLocation,
  aOrganization,
  aPersistedFilters,
  aSelectedListing,
  aUser,
  useAtom,
  useAtomValue,
  useSetAtom,
  type MapFiltersType,
} from '~/atoms'
import {
  type AppMapViewState,
  GoogleMapBase,
  googleBoundsToAppMapBounds,
  moveGoogleMapCamera,
  useGoogleMapCameraTween,
  type AppMapCameraOptions,
  type AppMapCenter,
  type AppMapRef,
} from '~/components'
import {
  MapView,
  useCurrentMapView,
  useEmbed,
  useHydrated,
  useIsSafari,
  useMapCardSizes,
} from '~/hooks'
import {
  DEFAULT_VIEW_STATE,
  merge,
  type DefaultMapViewState,
} from '~/utils'

type ListingMapProps = {
  mapRef: React.MutableRefObject<AppMapRef | null>
  children: React.ReactNode
  layers?: React.ReactNode
}

function safeParseFloat(float: string | null | undefined): number | undefined {
  if (typeof float !== 'string') return undefined

  const parsed = parseFloat(float)

  return isNaN(parsed) ? undefined : parsed
}

function getLatLngLiteral(
  center: AppMapCenter | google.maps.LatLng | null | undefined
): google.maps.LatLngLiteral | undefined {
  if (!center) return undefined

  if (Array.isArray(center)) {
    return {
      lat: center[1],
      lng: center[0],
    }
  }

  if (typeof center.lat === 'function' && typeof center.lng === 'function') {
    return {
      lat: center.lat(),
      lng: center.lng(),
    }
  }

  const literal = center as google.maps.LatLngLiteral

  return {
    lat: literal.lat,
    lng: literal.lng,
  }
}

function getGoogleAppMapRef(
  map: google.maps.Map,
  animateCameraRef: React.RefObject<
    ReturnType<typeof useGoogleMapCameraTween>['animateCamera']
  >
): AppMapRef {
  const moveTo = (options: AppMapCameraOptions) => {
    const center = getLatLngLiteral(options.center)
    const zoom = options.zoom
    const heading = options.heading ?? options.bearing
    const tilt = options.tilt ?? options.pitch
    const padding = options.padding

    if (options.duration && options.duration > 0) {
      animateCameraRef.current?.(
        {
          center,
          heading,
          padding,
          tilt,
          zoom,
        },
        { duration: options.duration }
      )
      return
    }

    moveGoogleMapCamera(map, {
      center,
      heading,
      padding,
      tilt,
      zoom,
    })
  }

  return {
    easeTo: moveTo,
    flyTo: moveTo,
    getBearing: () => map.getHeading() ?? 0,
    getBounds: () => map.getBounds(),
    getCenter: () => {
      const center = map.getCenter()
      if (!center) return null

      return {
        lat: center.lat(),
        lng: center.lng(),
      }
    },
    getPitch: () => map.getTilt() ?? 0,
    getZoom: () => map.getZoom(),
    jumpTo: moveTo,
    moveCamera: (options) => map.moveCamera(options),
    resize: () => {
      google.maps.event.trigger(map, 'resize')
    },
    triggerRepaint: () => {
      google.maps.event.trigger(map, 'resize')
    },
  }
}

function roundMapCoordinate(value: number) {
  return Number(value.toFixed(6))
}

function roundMapZoom(value: number) {
  return Number(value.toFixed(3))
}

function isSameMapViewState(
  previous: AppMapViewState | null,
  next: AppMapViewState
) {
  if (!previous) return false

  return (
    previous.bounds.east === next.bounds.east &&
    previous.bounds.north === next.bounds.north &&
    previous.bounds.south === next.bounds.south &&
    previous.bounds.west === next.bounds.west &&
    previous.lat === next.lat &&
    previous.lng === next.lng &&
    previous.zoom === next.zoom
  )
}

function hasMapCameraParams(searchParams: URLSearchParams) {
  return (
    searchParams.has('lat') ||
    searchParams.has('lng') ||
    searchParams.has('z') ||
    searchParams.has('b') ||
    searchParams.has('p')
  )
}

function isSameGoogleCamera(
  map: google.maps.Map,
  viewState: DefaultMapViewState,
  isSafari: boolean
) {
  const center = map.getCenter()
  const zoom = map.getZoom()

  if (!center || typeof zoom !== 'number') return false

  const heading = map.getHeading() ?? 0
  const tilt = map.getTilt() ?? 0

  return (
    roundMapCoordinate(center.lat()) ===
      roundMapCoordinate(viewState.latitude) &&
    roundMapCoordinate(center.lng()) ===
      roundMapCoordinate(viewState.longitude) &&
    roundMapZoom(zoom) === roundMapZoom(viewState.zoom) &&
    (isSafari ||
      (roundMapZoom(heading) === roundMapZoom(viewState.bearing ?? 0) &&
        roundMapZoom(tilt) === roundMapZoom(viewState.pitch ?? 0)))
  )
}

function GoogleMapRouteCameraSync({
  hasCameraParams,
  isSafari,
  locationKey,
  viewState,
}: {
  hasCameraParams: boolean
  isSafari: boolean
  locationKey: string
  viewState: DefaultMapViewState
}) {
  const map = useMap()

  useEffect(() => {
    if (!map || !hasCameraParams) return

    if (isSameGoogleCamera(map, viewState, isSafari)) return

    moveGoogleMapCamera(map, {
      center: {
        lat: viewState.latitude,
        lng: viewState.longitude,
      },
      heading: isSafari ? 0 : viewState.bearing,
      tilt: isSafari ? 0 : viewState.pitch,
      zoom: viewState.zoom,
    })
  }, [hasCameraParams, isSafari, locationKey, map, viewState])

  return null
}

function GoogleMapStateBridge({
  mapRef,
  persistFilters,
  syncMapSearchParams,
}: {
  mapRef: React.MutableRefObject<AppMapRef | null>
  persistFilters: (map: AppMapRef | null, filters: MapFiltersType) => void
  syncMapSearchParams: () => void
}) {
  const map = useMap()
  const { animateCamera } = useGoogleMapCameraTween()
  const animateCameraRef = useRef(animateCamera)
  const [mapFilters] = useAtom(aMapFilters)
  const mapFiltersRef = useRef(mapFilters)
  const persistFiltersRef = useRef(persistFilters)
  const setMapRef = useSetAtom(aMapRef)
  const setMapViewState = useSetAtom(aMapViewState)
  const mapViewStateRef = useRef<AppMapViewState | null>(null)
  const markLocation = useAtomValue(aMarkLocation)
  const setMarkLocation = useSetAtom(aMarkLocation)
  const markLocationRef = useRef(markLocation)
  const isMapEasing = useAtomValue(aIsMapEasing)
  const syncMapViewStateRef = useRef<() => void>(() => {})
  const syncMapSearchParamsRef = useRef(syncMapSearchParams)

  const syncMapViewState = useCallback(() => {
    if (!map) return

    const center = map.getCenter()
    const bounds = googleBoundsToAppMapBounds(map.getBounds())
    const zoom = map.getZoom()

    if (center && bounds && typeof zoom === 'number') {
      const nextMapViewState = {
        bounds: {
          east: roundMapCoordinate(bounds.east),
          north: roundMapCoordinate(bounds.north),
          south: roundMapCoordinate(bounds.south),
          west: roundMapCoordinate(bounds.west),
        },
        lat: roundMapCoordinate(center.lat()),
        lng: roundMapCoordinate(center.lng()),
        zoom: roundMapZoom(zoom),
      }

      if (!isSameMapViewState(mapViewStateRef.current, nextMapViewState)) {
        mapViewStateRef.current = nextMapViewState
        setMapViewState(nextMapViewState)
      }
    }

    if (!isMapEasing && markLocationRef.current) {
      markLocationRef.current = false
      setMarkLocation(false)
    }
  }, [isMapEasing, map, setMapViewState, setMarkLocation])

  useEffect(() => {
    mapFiltersRef.current = mapFilters
  }, [mapFilters])

  useEffect(() => {
    markLocationRef.current = markLocation
  }, [markLocation])

  useEffect(() => {
    animateCameraRef.current = animateCamera
  }, [animateCamera])

  useEffect(() => {
    persistFiltersRef.current = persistFilters
  }, [persistFilters])

  useEffect(() => {
    syncMapSearchParamsRef.current = syncMapSearchParams
  }, [syncMapSearchParams])

  useEffect(() => {
    syncMapViewStateRef.current = syncMapViewState
  }, [syncMapViewState])

  useEffect(() => {
    if (!map) return

    const nextMapRef = getGoogleAppMapRef(map, animateCameraRef)
    mapRef.current = nextMapRef
    setMapRef(nextMapRef)
    map.setOptions({
      draggableCursor: 'grab',
      draggingCursor: 'grabbing',
    })
    syncMapViewStateRef.current()

    return () => {
      persistFiltersRef.current(nextMapRef, mapFiltersRef.current)
      mapRef.current = null
      setMapRef(null)
    }
  }, [map, mapRef, setMapRef])

  useEffect(() => {
    if (!map) return

    const idleListener = map.addListener('idle', () => {
      startTransition(() => {
        syncMapViewStateRef.current()
        syncMapSearchParamsRef.current()
      })
    })

    return () => {
      idleListener.remove()
    }
  }, [map])

  return null
}

export default function ListingsMap(props: ListingMapProps) {
  const { mapRef, children, layers } = props
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const organization = useAtomValue(aOrganization)
  const user = useAtomValue(aUser)
  const [mapFilters, setMapFilters] = useAtom(aMapFilters)
  const mapFiltersRef = useRef(mapFilters)
  const [, setSelectedListing] = useAtom(aSelectedListing)
  const mapStyle = useAtomValue(aMapStyle)
  const currentMapView = useCurrentMapView()
  const mapCardSizes = useMapCardSizes()
  const [markLocation] = useAtom(aMarkLocation)
  const isMapEasing = useAtomValue(aIsMapEasing)
  const isSafari = useIsSafari()
  const [persistedFilters, setPersistedFilters] = useAtom(aPersistedFilters)
  const mapViewState = useAtomValue(aMapViewState)
  const hydrated = useHydrated()
  const { isEmbed } = useEmbed()
  const hasCameraParams = hasMapCameraParams(searchParams)
  const previousPathnameRef = useRef<string | null>(null)
  const routeCameraSyncEntryKeyRef = useRef<string | null>(null)
  const routeCameraSyncAllowedRef = useRef(false)
  const routeCameraSyncEntryKey = `${location.key}:${location.pathname}:${location.search}`

  if (routeCameraSyncEntryKeyRef.current !== routeCameraSyncEntryKey) {
    const previousPathname = previousPathnameRef.current

    routeCameraSyncEntryKeyRef.current = routeCameraSyncEntryKey
    // Freeze this per history entry. After closing a listing route, a later
    // unrelated render must not re-enable sync and replay stale URL camera params.
    routeCameraSyncAllowedRef.current =
      hasCameraParams &&
      location.pathname === '/' &&
      (previousPathname === null || previousPathname === '/')
  }

  const shouldSyncRouteCamera = routeCameraSyncAllowedRef.current

  const persistFilters = useCallback(
    (map: AppMapRef | null, filters: MapFiltersType) => {
      if (!map) return

      const preserve = Boolean(user?.options?.map?.preserve)

      if (!preserve) return

      const center = map.getCenter()
      const zoom = map.getZoom()
      const bearing = map.getBearing?.() ?? 0
      const pitch = map.getPitch?.() ?? 0

      if (!center || typeof zoom !== 'number') return

      setPersistedFilters({
        ...filters,
        lng: center.lng.toFixed(6),
        lat: center.lat.toFixed(6),
        z: zoom.toFixed(3),
        b: bearing === 0 ? '0' : bearing.toFixed(3),
        p: pitch === 0 ? '0' : pitch.toFixed(3),
      })
    },
    [setPersistedFilters, user]
  )

  useBeforeUnload(() => {
    persistFilters(mapRef.current, mapFilters)
  })

  useEffect(() => {
    mapFiltersRef.current = mapFilters
  }, [mapFilters])

  useEffect(() => {
    return () => {
      persistFilters(mapRef.current, mapFiltersRef.current)
      setSelectedListing(null)
    }
  }, [mapRef, persistFilters, setSelectedListing])

  const updateSearchParams = useCallback(() => {
    if (currentMapView !== MapView.Map || !mapRef.current) return

    const center = mapRef.current.getCenter()
    const zoom = mapRef.current.getZoom()
    const bearing = mapRef.current.getBearing?.() ?? 0
    const pitch = mapRef.current.getPitch?.() ?? 0

    if (center && typeof zoom === 'number') {
      const nextMapPositionFilters = {
        lng: center.lng.toFixed(6),
        lat: center.lat.toFixed(6),
        z: zoom.toFixed(3),
        b: isSafari ? undefined : bearing ? bearing.toFixed(3) : '0',
        p: isSafari ? undefined : pitch ? pitch.toFixed(3) : '0',
      }
      const currentFilters = mapFiltersRef.current
      const currentMapPositionFilters = {
        lng: currentFilters.lng,
        lat: currentFilters.lat,
        z: currentFilters.z,
        b: currentFilters.b?.toString(),
        p: currentFilters.p?.toString(),
      }

      if (
        currentMapPositionFilters.lng === nextMapPositionFilters.lng &&
        currentMapPositionFilters.lat === nextMapPositionFilters.lat &&
        currentMapPositionFilters.z === nextMapPositionFilters.z &&
        currentMapPositionFilters.b === nextMapPositionFilters.b &&
        currentMapPositionFilters.p === nextMapPositionFilters.p
      ) {
        return
      }

      setMapFilters({
        ...currentFilters,
        ...nextMapPositionFilters,
      })
    }
  }, [currentMapView, isSafari, mapRef, setMapFilters])

  useEffect(() => {
    updateSearchParams()
  }, [currentMapView, updateSearchParams])

  useEffect(() => {
    document.documentElement.style.overflowY = 'hidden'
    return () => {
      document.documentElement.style.overflowY = 'auto'
    }
  }, [])

  useEffect(() => {
    previousPathnameRef.current = location.pathname
  }, [location.pathname])

  const initialViewState: DefaultMapViewState = useMemo(() => {
    const bearingAndPitch = isSafari
      ? {}
      : {
          pitch: safeParseFloat(searchParams.get('p')),
          bearing: safeParseFloat(searchParams.get('b')),
        }

    const userViewState =
      user?.options?.map?.initialViewState !== undefined
        ? user.options.map.initialViewState
        : {}

    const persisted =
      !!user?.options?.map?.preserve &&
      persistedFilters &&
      Object.keys(persistedFilters).length > 0
        ? {
            latitude: safeParseFloat(persistedFilters.lat),
            longitude: safeParseFloat(persistedFilters.lng),
            zoom: safeParseFloat(persistedFilters.z),
            bearing: safeParseFloat(persistedFilters.b),
            pitch: safeParseFloat(persistedFilters.p),
          }
        : {}

    const params = {
      latitude: safeParseFloat(searchParams.get('lat')),
      longitude: safeParseFloat(searchParams.get('lng')),
      zoom: safeParseFloat(searchParams.get('z')),
    }

    return merge<DefaultMapViewState>(
      DEFAULT_VIEW_STATE,
      organization!.options.initialViewState,
      userViewState,
      persisted,
      bearingAndPitch,
      params,
      {
        padding: { left: mapCardSizes.list, top: 0, right: 0, bottom: 0 },
      }
    )
  }, [mapCardSizes.list, organization, persistedFilters, searchParams, user, isSafari])

  useEffect(() => {
    mapRef.current?.resize?.()
  }, [mapCardSizes.filters, mapRef])

  if (!organization!.options.initialViewState) {
    return (
      <div>
        <p>Map view state not set</p>
      </div>
    )
  }

  if (!!user?.options?.map?.preserve && !hydrated) return null

  return (
    <div
      id="map-container"
      className="relative h-dvh w-dvw"
      style={{
        width: mapCardSizes.filters
          ? `calc(100dvw - ${mapCardSizes.filters}px)`
          : '100dvw',
      }}>
      <GoogleMapBase
        defaultCenter={{
          lat: initialViewState.latitude,
          lng: initialViewState.longitude,
        }}
        defaultZoom={initialViewState.zoom}
        defaultHeading={isSafari ? 0 : initialViewState.bearing}
        defaultTilt={isSafari ? 0 : initialViewState.pitch}
        mapStyle={mapStyle}
        minZoom={4}
        maxZoom={18}
        gestureHandling="greedy"
        disableDefaultUI
        className="h-full w-full"
        style={{ width: '100%', height: '100%', userSelect: 'none' }}>
        <GoogleMapStateBridge
          mapRef={mapRef}
          persistFilters={persistFilters}
          syncMapSearchParams={updateSearchParams}
        />
        <GoogleMapRouteCameraSync
          hasCameraParams={shouldSyncRouteCamera}
          isSafari={isSafari}
          locationKey={location.key}
          viewState={initialViewState}
        />
        {layers}
        {markLocation && mapViewState && !isMapEasing && (
          <AdvancedMarker
            position={{
              lat: mapViewState.lat,
              lng: mapViewState.lng,
            }}>
            <div className="h-5 w-5 rounded-full border-2 border-white bg-primary shadow-md" />
          </AdvancedMarker>
        )}
        {children}
      </GoogleMapBase>
    </div>
  )
}
